import { Controller } from "@hotwired/stimulus"

// The header's ⌘K box used to only move the cursor into itself: no search, no
// handler for Enter. This queries /backoffice/search and lets the results be
// driven from the keyboard.
export default class extends Controller {
  static targets = ["input", "panel", "list", "empty", "hint", "minHint", "modifier"]
  static values = {
    url: String,
    minLength: { type: Number, default: 2 },
    debounce: { type: Number, default: 180 },
  }

  connect() {
    if (this.hasModifierTarget && this.isMac()) this.modifierTarget.textContent = "⌘"
    this.results = []
    this.cursor = -1
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    this.onShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        this.inputTarget.focus()
        this.inputTarget.select()
      }
    }
    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onShortcut)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onShortcut)
    if (this.timer) clearTimeout(this.timer)
    if (this.controllerAbort) this.controllerAbort.abort()
  }

  // Typing is debounced so a fast typist makes one request, not eight.
  search() {
    if (this.timer) clearTimeout(this.timer)
    const term = this.inputTarget.value.trim()

    if (term.length < this.minLengthValue) {
      this.results = []
      this.render()
      // One character used to close the panel without a word; it now says why
      // nothing is searched yet. An empty box stays closed.
      if (term.length > 0) {
        this.emptyTarget.classList.add("hidden")
        if (this.hasMinHintTarget) this.minHintTarget.classList.remove("hidden")
        this.open()
      } else {
        this.close()
      }
      return
    }
    if (this.hasMinHintTarget) this.minHintTarget.classList.add("hidden")

    this.timer = setTimeout(() => this.fetchResults(term), this.debounceValue)
  }

  async fetchResults(term) {
    // A slower earlier response must not overwrite a newer one.
    if (this.controllerAbort) this.controllerAbort.abort()
    this.controllerAbort = new AbortController()

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(term)}`, {
        headers: { Accept: "application/json" },
        signal: this.controllerAbort.signal,
      })
      if (!response.ok) throw new Error(response.status)
      const data = await response.json()
      this.results = data.results || []
      this.cursor = this.results.length ? 0 : -1
      this.render()
      this.open()
    } catch (error) {
      if (error.name === "AbortError") return
      this.results = []
      this.render()
      this.open()
    }
  }

  render() {
    this.listTarget.innerHTML = ""

    if (!this.results.length) {
      this.emptyTarget.classList.remove("hidden")
      return
    }
    this.emptyTarget.classList.add("hidden")

    this.results.forEach((result, index) => {
      const item = document.createElement("a")
      item.href = result.url || "#"
      item.dataset.index = index
      item.dataset.action = "mouseenter->command-palette#hover"
      item.className =
        "flex items-center justify-between gap-3 px-4 py-2.5 text-sm no-underline " +
        (index === this.cursor ? "bg-gray-100 dark:bg-white/10" : "hover:bg-gray-50 dark:hover:bg-white/5")

      const left = document.createElement("span")
      left.className = "min-w-0 truncate text-gray-800 dark:text-gray-100"
      left.textContent = result.label
      item.appendChild(left)

      const right = document.createElement("span")
      right.className = "shrink-0 text-xs text-gray-400"
      right.textContent = [result.sublabel, result.group_label || result.group].filter(Boolean).join(" · ")
      item.appendChild(right)

      this.listTarget.appendChild(item)
    })
  }

  hover(event) {
    this.cursor = Number(event.currentTarget.dataset.index)
    this.render()
  }

  navigate(event) {
    if (event.key === "Escape") {
      this.close()
      this.inputTarget.blur()
      return
    }
    if (!this.results.length) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.cursor = (this.cursor + 1) % this.results.length
      this.render()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.cursor = (this.cursor - 1 + this.results.length) % this.results.length
      this.render()
    } else if (event.key === "Enter") {
      event.preventDefault()
      const chosen = this.results[this.cursor]
      if (chosen?.url) window.location.href = chosen.url
    }
  }

  open() {
    this.panelTarget.classList.remove("hidden")
  }

  focus() {
    this.inputTarget.focus()
  }

  isMac() {
    const platform = navigator.userAgentData?.platform || navigator.platform || ""
    return /mac|iphone|ipad/i.test(platform)
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }
}
